import { tagStyle } from '../../lib/jobTags';
import React, { useEffect, useState } from 'react';
import { Check, ChevronsUpDown, User, Users, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { easerGet } from '../../lib/easerApi';

/**
 * Searchable picker of another Easer whose planning is shown read-only.
 * value: null (my planning) or {id, name}.
 */
export function EaserPicker({ value, onChange, onUnauthorized }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || items !== null) return;
    easerGet('/api/easer/colleagues')
      .then((res) => setItems(res.data.items))
      .catch((err) => {
        if (err?.response?.status === 401) return onUnauthorized();
        setItems([]);
        setError('Liste des Easers indisponible');
      });
  }, [open, items, onUnauthorized]);

  return (
    <div className="flex items-center gap-1" data-testid="easer-picker">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" role="combobox" aria-expanded={open} className={`h-9 max-w-[200px] justify-between ${value ? 'border-[hsl(var(--accent))]/60 text-[hsl(var(--accent))]' : ''}`} data-testid="easer-picker-trigger">
            {value ? <Users className="h-4 w-4 mr-1 flex-shrink-0" /> : <User className="h-4 w-4 mr-1 flex-shrink-0" />}
            <span className="truncate">{value ? value.name : 'Mon planning'}</span>
            <ChevronsUpDown className="h-3.5 w-3.5 ml-1 opacity-60 flex-shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[280px]" align="end">
          <Command filter={(value, search) => (value === '__me' || value.toLowerCase().includes(search.toLowerCase().trim()) ? 1 : 0)}>
            <CommandInput placeholder="Rechercher un Easer..." data-testid="easer-picker-search" />
            <CommandList>
              <CommandEmpty>{items === null ? 'Chargement...' : error || 'Aucun Easer trouvé'}</CommandEmpty>
              <CommandGroup>
                <CommandItem value="__me" onSelect={() => { onChange(null); setOpen(false); }} data-testid="easer-picker-me">
                  <Check className={`h-4 w-4 mr-2 ${value ? 'opacity-0' : 'opacity-100'}`} /> Mon planning
                </CommandItem>
                {(items || []).map((c) => (
                  <CommandItem key={c.id} value={`${c.name} ${c.login}`} onSelect={() => { onChange(c); setOpen(false); }} data-testid="easer-picker-item">
                    <Check className={`h-4 w-4 mr-2 ${value?.id === c.id ? 'opacity-100' : 'opacity-0'}`} /> <span className="flex-1 truncate">{c.name}</span>{(c.tags || []).map((tg) => <span key={tg} className={`ml-1 rounded-full border px-1.5 py-0 text-[10px] font-medium ${tagStyle(tg).badge}`}>{tg}</span>)}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {value && (
        <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => onChange(null)} aria-label="Revenir à mon planning" data-testid="easer-picker-clear"><X className="h-4 w-4" /></Button>
      )}
    </div>
  );
}
